import { useEffect, useState } from "react";
import { useDebounced } from "./useDebounced";

interface ResponsiveState {
  windowWidth: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Breakpoints aligned with Bootstrap 5 standards
// Mobile: < 768px (xs, sm)
// Tablet: 768px - 991.98px (md)
// Desktop: >= 992px (lg, xl, xxl)
const MOBILE_MAX = 767.98;      // Mobile: 0 to 767.98px
const TABLET_MIN = 768;         // Tablet: 768px to 991.98px
const TABLET_MAX = 991.98;      // Desktop: 992px and above
const DESKTOP_MIN = 992;

const useResponsive = (): ResponsiveState => {
  // Handle SSR - check if window is available
  const getInitialWidth = () => {
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 0;
  };

  const [windowWidth, setWindowWidth] = useState(getInitialWidth());

  const getResponsiveState = (width: number) => {
    const isMobile = width <= MOBILE_MAX;
    const isTablet = width >= TABLET_MIN && width <= TABLET_MAX;
    const isDesktop = width >= DESKTOP_MIN;

    return { isMobile, isTablet, isDesktop };
  };

  const initialState = getResponsiveState(windowWidth);
  const [isMobile, setIsMobile] = useState(initialState.isMobile);
  const [isTablet, setIsTablet] = useState(initialState.isTablet);
  const [isDesktop, setIsDesktop] = useState(initialState.isDesktop);

  const handleResize = useDebounced(200, () => {
    if (typeof window === "undefined") return;
    
    const width = window.innerWidth;
    setWindowWidth(width);
    const state = getResponsiveState(width);
    setIsMobile(state.isMobile);
    setIsTablet(state.isTablet);
    setIsDesktop(state.isDesktop);
  });

  useEffect(() => {
    // Handle SSR - only add listener if window is available
    if (typeof window === "undefined") return;

    // Set initial state on mount (in case of hydration mismatch)
    const width = window.innerWidth;
    const state = getResponsiveState(width);
    setWindowWidth(width);
    setIsMobile(state.isMobile);
    setIsTablet(state.isTablet);
    setIsDesktop(state.isDesktop);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return { windowWidth, isMobile, isTablet, isDesktop };
};

export default useResponsive;
